import { t } from "i18next";
import type { AuthProvider } from "@refinedev/core";
import type { QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";

import {
  createAuthProvider,
  type RegisterParams as LibRegisterParams,
} from "@libertify/auth/refine-adapter";
import { clearSuperProperties } from "@libertify.frontend/analytics";

import { API_URL, PERSIST_STORE_KEY } from "@/constants";
import { authService } from "services/authService";
import instance from "services/api";
import { resetAllSlices } from "store/utils";
import { clearSegmentIdentity } from "services/analyticsConsent";
import {
  createUserTraits,
  persistOnboardingProperties,
  trackEventWithoutHooks,
  identifyWithoutHooks,
} from "hooks/useAnalytics";
import { toAuthMethod } from "utils/analytics";
import { AnalyticsEvents } from "types/analytics";
import type { Application, User as ApiUser } from "types";
import { addDateTz } from "pages/media/utils";
import { SCALEKIT_CLIENT_ID, SCALEKIT_ENV_URL } from "@/pages/auth/config";

export type RegisterParams = LibRegisterParams<Application>;

// QueryClient instance for clearing cache on logout.
// Set via setupAuthProvider() in App.tsx (lazy init mirrors pre-extraction behavior).
let queryClient: QueryClient | null = null;

/**
 * Setup function to inject QueryClient into authProvider.
 * MUST be called before authProvider is used (in App.tsx).
 */
export const setupAuthProvider = (qc: QueryClient) => {
  queryClient = qc;
};

// Admin's dayjs/tz-aware "is the user freshly created?" implementation.
const isNewUser = (createdDate: string, thresholdMinutes = 5): boolean => {
  const userCreatedDate = dayjs(addDateTz(createdDate));
  const now = dayjs();
  const timeDiffMinutes = now.diff(userCreatedDate, "minute");
  return timeDiffMinutes < thresholdMinutes;
};

export const authProvider: AuthProvider = createAuthProvider<ApiUser>({
  service: authService,
  axiosInstance: instance,
  apiUrl: API_URL,
  queryClient: () => queryClient,
  i18n: { t },
  analytics: {
    trackEvent: trackEventWithoutHooks,
    identify: identifyWithoutHooks,
    persistOnboardingProperties,
    createUserTraits,
    toAuthMethod,
  },
  events: {
    Login: AnalyticsEvents.Login,
    LoginFailed: AnalyticsEvents.LoginFailed,
    UserActivated: AnalyticsEvents.UserActivated,
  },
  scalekit: {
    envUrl: SCALEKIT_ENV_URL,
    clientId: SCALEKIT_CLIENT_ID,
    // Lazy thunk so the origin is read at logout time, not at module-load
    // time — required for preview deploys / iframe embeds where the origin
    // can differ from when authProvider was first imported.
    postLogoutRedirectUri: () => `${globalThis.location.origin}/login`,
  },
  /**
   * Admin-specific logout cleanup. Runs after the lib has cleared auth state
   * and flushed React Query, before the post-logout redirect. Ordering inside
   * this hook matches admin's pre-extraction sequence:
   *   1. resetAllSlices() — reset Zustand in-memory state (reads from empty localStorage)
   *   2. Sentry.setUser(null) — clear error-tracking identity
   *   3. clearSuperProperties() — clear analytics super-properties
   *   4. clearSegmentIdentity() — drop Segment's user id, traits and cookies
   *   5. localStorage.removeItem(PERSIST_STORE_KEY) — drop the persisted Zustand key
   *      LAST, after resetAllSlices's persist-middleware writes settle, so no
   *      residual state survives.
   */
  onLogoutCleanup: () => {
    resetAllSlices();
    Sentry.setUser(null);
    clearSuperProperties();
    clearSegmentIdentity();
    localStorage.removeItem(PERSIST_STORE_KEY);
  },
  isNewUser,
});
